import { EasyClaimDocumentDto } from 'src/easyclaim.api.client.typescript/src/models';

import { getEasyClaimAuthorizationHeaders } from '../easyclaim.app.configuration/ciapiAuthorization';
import { blobToDataURL } from '../easyclaim.app.libs/blobToDataUrl';
import { reportError } from '../easyclaim.app.libs/reportError';

import { Image, SelectState } from './ImageSelectionService';

export class EasyClaimDocumentService {
	private readonly basePath: string;
	private readonly apiKey: string;

	constructor(basePath: string, apiKey: string) {
		this.basePath = basePath;
		this.apiKey = apiKey;
	}

	public getImageUrl(caseId: string, imageId: string) {
		return `${this.basePath}/api/v1/image?caseId=${caseId}&imageId=${imageId}`;
	}

	public getImageForDocument(
		caseId: string,
		imageId: string,
		document: EasyClaimDocumentDto,
		componentId: string
	): Promise<Image> {
		return this.fetchImageFromUrl(
			`${this.basePath}/api/v1/image?caseId=${caseId}&imageId=${imageId}`,
			document,
			componentId,
			caseId
		);
	}

	private async fetchImageFromUrl(
		url: string,
		document: EasyClaimDocumentDto,
		componentId: string,
		caseId: string
	): Promise<Image> {
		const response = await fetch(url, {
			headers: {
				'X-ApiKey': this.apiKey,
				...getEasyClaimAuthorizationHeaders(),
			},
		});
		const blob = await response.blob();

		const dataUrl: string = await blobToDataURL(blob);

		const dataUrlHead = dataUrl.substring(0, dataUrl.indexOf(','));
		const fileContent = dataUrl.substring(dataUrl.indexOf(',') + 1);

		const imageDocument: EasyClaimDocumentDto = {
			documentId: document.documentId,
			documentType: document.documentType,
			fileName: document.fileName,
			fileContent: fileContent,
			creationDate: document.creationDate,
			validationResults: document.validationResults,
			documentFeatureValues: document.documentFeatureValues,
		};

		const result: Image = {
			componentId: componentId, //"registrationDocument",
			selectState: SelectState.Uploaded,
			dataUrlHead: dataUrlHead,
			document: imageDocument,
			uploadResult: {
				caseId: caseId,
				documentId: document.documentId,
				creationDate: document.creationDate,
				validationResults: document.validationResults,
				documentFeatureValues: document.documentFeatureValues,
			},
		};

		return result;
	}

	/**
	 * Builds an Image shell with document metadata only — no network request.
	 * The actual file content will be fetched lazily by SelectImage on mount.
	 */
	public buildImageShell(
		caseId: string,
		document: EasyClaimDocumentDto,
		componentId: string
	): Image {
		return {
			componentId,
			selectState: SelectState.Uploaded,
			document: {
				documentId: document.documentId,
				documentType: document.documentType,
				fileName: document.fileName,
				fileContent: undefined,
				creationDate: document.creationDate,
				validationResults: document.validationResults,
				documentFeatureValues: document.documentFeatureValues,
			},
			uploadResult: {
				caseId,
				documentId: document.documentId,
				creationDate: document.creationDate,
				validationResults: document.validationResults,
				documentFeatureValues: document.documentFeatureValues,
			},
		};
	}

	public getThumbnailUrl(caseId: string, imageId: string, width: number, height: number) {
		return `${this.basePath}/api/v1/image/thumbnail?caseId=${caseId}&imageId=${imageId}&width=${width}&height=${height}`;
	}

	public getThumbnailSrcSet(caseId: string, imageId: string) {
		return `${this.getThumbnailUrl(caseId, imageId, 360, 360)} 1x,
        ${this.getThumbnailUrl(caseId, imageId, 540, 540)} 1.5x,
        ${this.getThumbnailUrl(caseId, imageId, 720, 720)} 2x,
        ${this.getThumbnailUrl(caseId, imageId, 1440, 1440)} 4x`;
	}

	public async getLogoAnyFormat(caseId: string): Promise<string> {
		const url = `${this.basePath}/api/v1/logo/any?caseId=${caseId}`;

		try {
			const response = await fetch(url, {
				headers: {
					'X-ApiKey': this.apiKey,
					...getEasyClaimAuthorizationHeaders(),
				},
			});

			if (!response.ok) {
				return '';
			}
			const blob = await response.blob();

			return await blobToDataURL(blob);
		} catch (error) {
			reportError(error, { context: 'EasyClaimDocumentService.getLogoAnyFormat', caseId });

			return '';
		}
	}

	public getDocumentUrl(caseId: string, documentId: string) {
		return `${this.basePath}/api/v1/document?caseId=${caseId}&documentId=${documentId}`;
	}

	public getFileUrl(fileId: number) {
		return `${this.basePath}/api/v1/file/any?fileId=${fileId}`;
	}
}
